<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Roster Vault</p>
          <h2>Favorite characters</h2>
          <p>Your saved characters, profile refreshes and quick filtering by role, realm or score.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Saved: <span>{{ favorites.length }}</span></span>
        <span v-if="search" class="filter-pill">Search: <span>{{ search }}</span></span>
        <span class="filter-pill">Sort: <span>{{ sortBy }}</span></span>
      </div>
      <div role="group" class="quick-search-field">
        <input
          id="favorite-search"
          v-model.trim="search"
          class="input"
          aria-label="Search favorites"
          placeholder="Search by character name"
          type="search"
        />
      </div>
      <div class="page-actions">
        <button
          class="btn-icon-outline"
          type="button"
          :disabled="refreshingAll || !favorites.length"
          aria-label="Refresh all characters"
          title="Refresh all characters"
          @click="refreshAllFavorites(false)"
        >
          <i class="fa-solid fa-rotate"></i>
        </button>
        <button class="btn-icon-outline" type="button" aria-label="Reset filters" title="Reset filters" @click="resetFilters">
          <i class="fa-solid fa-rotate-left"></i>
        </button>
        <button class="btn-icon" type="button" aria-label="Filters" title="Filters" @click="openFilters">
          <i class="fa-solid fa-sliders"></i>
        </button>
      </div>
    </div>

    <form class="card form-card" @submit.prevent="addByUrl">
      <div class="character-url-row">
        <div role="group" class="field character-url-field">
          <label class="label">Raider.io character URL</label>
          <input
            v-model.trim="characterUrl"
            class="input"
            placeholder="https://raider.io/characters/eu/howling-fjord/Name"
            type="url"
          />
        </div>

        <button class="btn-icon character-url-submit" type="submit" :disabled="loading" aria-label="Add character" title="Add character">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </form>

    <div
      v-if="isFiltersOpen"
      class="app-modal-backdrop filter-drawer-backdrop"
      :class="{ 'is-closing': closingFilters }"
      @click.self="closeFilters"
    >
      <aside class="app-filter-drawer" role="dialog" aria-modal="true" aria-labelledby="favorite-filters-title">
        <header>
          <h2 id="favorite-filters-title">Favorite filters</h2>
          <p>Search and sort saved characters.</p>
        </header>
        <section>
          <form class="form filters-grid" @submit.prevent="applyFilters">
            <div role="group" class="field filter-field">
              <label class="label">Sort by</label>
              <select v-model="draftSortBy" class="select">
                <option value="addedAt">Date added</option>
                <option value="score">Mythic+ score</option>
                <option value="itemLevel">Item level</option>
                <option value="name">Name</option>
              </select>
            </div>
          </form>
        </section>
        <footer>
          <button class="btn-outline" type="button" @click="closeFilters">Cancel</button>
          <button class="btn" type="button" @click="applyFilters">Apply</button>
        </footer>
      </aside>
    </div>

    <AppEmptyState
      v-if="!favorites.length"
      eyebrow="Favorites"
      icon="fa-solid fa-star"
      title="No saved characters yet"
      message="Add a Raider.io character URL or save someone from Mythic+ runs to start building your roster vault."
      :actions="[
        { to: '/mythic-plus', label: 'Browse Mythic+', icon: 'fa-solid fa-dungeon' }
      ]"
    />

    <AppEmptyState
      v-else-if="!sortedFavorites.length"
      eyebrow="No matches"
      icon="fa-solid fa-magnifying-glass"
      title="Nothing matched these filters"
      message="Try a different character name, reset search, or change the sorting and filter options."
    />

    <div v-else class="favorites-grid">
      <article v-for="favorite in sortedFavorites" :key="favorite.id" class="card favorite-card">
        <button
          class="btn-icon-outline favorite-card-menu-button"
          type="button"
          aria-label="Open character menu"
          title="More actions"
          @click.stop="toggleFavoriteMenu(favorite.id)"
        >
          <i class="fa-solid fa-ellipsis-vertical"></i>
        </button>
        <Transition name="favorite-menu">
          <div v-if="activeMenuFavoriteId === favorite.id" class="favorite-context-menu" @click.stop>
            <button type="button" @click="refreshFavoriteFromMenu(favorite)">
              <i class="fa-solid fa-rotate"></i>
              <span>Refresh</span>
            </button>
            <button type="button" @click="addToCompareFromMenu(favorite)">
              <i class="fa-solid fa-code-compare"></i>
              <span>Add to compare</span>
            </button>
            <button type="button" @click="removeFavoriteFromMenu(favorite.id)">
              <i class="fa-solid fa-trash"></i>
              <span>Remove</span>
            </button>
          </div>
        </Transition>

        <header class="favorite-card-shell">
          <section class="favorite-portrait-area">
            <div class="favorite-avatar-wrap">
              <img
                v-if="favorite.profile.thumbnail_url"
                class="avatar-image"
                :src="favorite.profile.thumbnail_url"
                :alt="favorite.profile.name"
                loading="lazy"
              >
              <span class="faction-mark" :class="`is-${factionKey(favorite.profile.faction)}`">
                <img :src="factionIcon(favorite.profile.faction)" :alt="favorite.profile.faction || 'Faction'" loading="lazy">
              </span>
            </div>

            <div class="favorite-more-info">
              <span>More info on</span>
              <div>
                <a
                  v-for="service in serviceLinks(favorite.profile)"
                  :key="`${favorite.id}-${service.key}`"
                  :href="service.href"
                  target="_blank"
                  rel="noreferrer"
                  :aria-label="`Open ${service.label} profile`"
                  :title="service.label"
                >
                  <img :src="service.icon" :alt="service.label" loading="lazy">
                </a>
              </div>
            </div>
          </section>

          <section class="favorite-main">
            <div class="favorite-title">
              <h3>
                <span :style="favoriteNameStyle(favorite.profile.class)">{{ favorite.profile.name }}</span>
                <img
                  v-if="classIcon(favorite.profile.class)"
                  class="favorite-class-icon"
                  :src="classIcon(favorite.profile.class)"
                  :alt="favorite.profile.class"
                  loading="lazy"
                >
              </h3>
              <strong>{{ favorite.profile.class }}</strong>
            </div>

            <dl class="favorite-meta">
              <div>
                <dt>Spec:</dt>
                <dd>{{ favorite.profile.active_spec_name || '-' }}</dd>
              </div>
              <div>
                <dt>Race:</dt>
                <dd>{{ favorite.profile.race || '-' }}</dd>
              </div>
              <div>
                <dt>Faction:</dt>
                <dd>{{ favorite.profile.faction || '-' }}</dd>
              </div>
              <div>
                <dt>Guild:</dt>
                <dd>{{ favorite.profile.guild?.name || 'No guild' }}</dd>
              </div>
              <div>
                <dt>Realm:</dt>
                <dd>{{ favorite.profile.realm }} ({{ favorite.profile.region.toUpperCase() }})</dd>
              </div>
              <div class="favorite-gear-line">
                <dt>Gear:</dt>
                <dd class="favorite-gear-row">
                  <!-- <span v-if="weaponItems(favorite.profile).length">Weapon</span> -->
                  <a
                    v-for="item in weaponItems(favorite.profile)"
                    :key="`${favorite.id}-weapon-${item.slotKey}`"
                    class="mini-gear-icon"
                    :href="wowheadItemUrl(item)"
                    :data-wowhead="wowheadItemData(item)"
                    target="_blank"
                    rel="noreferrer"
                    :title="item.name"
                  >
                    <img :src="itemIconUrl(item)" :alt="item.name" loading="eager">
                  </a>
                  <!-- <span>|</span> -->
                  <a
                    v-for="item in trinketSlots(favorite.profile)"
                    :key="`${favorite.id}-trinket-${item.slotKey}`"
                    class="mini-gear-icon"
                    :class="{ 'is-empty': !item.item_id && !item.id }"
                    :href="wowheadItemUrl(item)"
                    :data-wowhead="wowheadItemData(item)"
                    target="_blank"
                    rel="noreferrer"
                    :title="item.name"
                  >
                    <img v-if="itemIconUrl(item)" :src="itemIconUrl(item)" :alt="item.name" loading="eager">
                  </a>
                </dd>
              </div>
            </dl>
          </section>

          <aside class="favorite-score-panel">
            <div>
              <span>RIO score</span>
              <strong>{{ bestScore(favorite.profile) }}</strong>
            </div>
            <div>
              <span>Item level</span>
              <strong>{{ itemLevel(favorite.profile) }}</strong>
            </div>
          </aside>
        </header>

        <section class="favorite-tabs">
          <button class="btn-sm-outline" type="button" @click="openRuns(favorite)">
            <i class="fa-solid fa-gauge-high"></i> M+ Runs
          </button>
          <button class="btn-sm-outline" type="button" @click="openTalents(favorite)">
            <i class="fa-solid fa-code-branch"></i> Talents
          </button>
          <button class="btn-sm-outline" type="button" @click="openGear(favorite)">
            <i class="fa-solid fa-shirt"></i> Gear
          </button>
        </section>

        <footer class="favorite-stats">
          <div>
            <strong>{{ favorite.profile.achievement_points || '-' }}</strong>
            <span>Achievement points</span>
          </div>
          <div>
            <strong>{{ mythicPlusSpecRank(favorite.profile, 'realm') }}</strong>
            <span>Rank in realm</span>
          </div>
          <div>
            <strong>{{ mythicPlusSpecRank(favorite.profile, 'world') }}</strong>
            <span>Rank in the world</span>
          </div>
          <div>
            <strong>{{ raidProgressLabel(favorite.profile) }}</strong>
            <span>Raid progress</span>
          </div>
        </footer>
      </article>
    </div>

    <div
      v-if="activeRunsFavorite"
      class="app-modal-backdrop"
      :class="{ 'is-closing': closingRuns }"
      @click.self="closeRuns"
    >
      <section class="app-modal runs-modal" role="dialog" aria-modal="true" aria-labelledby="runs-modal-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Mythic Plus Runs</p>
            <h2 id="runs-modal-title">{{ activeRunsFavorite.profile.name }}</h2>
          </div>
          <button class="btn-icon-outline" type="button" aria-label="Close best runs" @click="closeRuns">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div class="runs-mode-switch view-switch" aria-label="Run list">
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': runsMode === 'best' }"
            type="button"
            @click="runsMode = 'best'"
          >
            Best runs
          </button>
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': runsMode === 'recent' }"
            type="button"
            @click="runsMode = 'recent'"
          >
            Recent runs
          </button>
        </div>

        <section class="table-wrap">
          <table class="table favorite-runs-table">
            <thead>
              <tr>
                <th>Dungeon</th>
                <th>Level</th>
                <th>Score</th>
                <th>Affixes</th>
                <th>Time</th>
                <th>Up</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="run in activeRuns(activeRunsFavorite.profile)" :key="run.url || `${run.dungeon}-${run.mythic_level}-${run.completed_at}`">
                <td class="dungeon-cell-compact">
                  <a :href="run.url || activeRunsFavorite.profile.profile_url" target="_blank" rel="noreferrer">
                    <img v-if="dungeonRunIcon(run)" :src="dungeonRunIcon(run)" :alt="dungeonRunName(run)" loading="lazy">
                    <strong>{{ dungeonRunName(run) }}</strong>
                  </a>
                </td>
                <td class="rank-cell">{{ run.mythic_level || '-' }}</td>
                <td class="score-cell">{{ formatScore(run.score) }}</td>
                <td>
                  <span class="affix-list compact">
                    <a
                      v-for="affix in run.affixes || []"
                      :key="affix.id"
                      :href="wowheadAffixUrl(affix)"
                      target="_blank"
                      rel="noreferrer"
                      :title="affix.name"
                    >
                      <img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy">
                    </a>
                  </span>
                </td>
                <td class="time-cell"><span>{{ runTime(run) }}</span></td>
                <td>{{ upgradeStars(run) }}</td>
                <td>
                  <a v-if="run.url" class="ranking-link-icon" :href="run.url" target="_blank" rel="noreferrer" aria-label="Open run">
                    <i class="fa-solid fa-link"></i>
                  </a>
                  <span v-else>-</span>
                </td>
              </tr>
              <tr v-if="!activeRuns(activeRunsFavorite.profile).length" class="is-empty-row">
                <td colspan="7">No {{ runsMode }} runs found for this character.</td>
              </tr>
            </tbody>
          </table>
        </section>
      </section>
    </div>

    <div
      v-if="activeGearFavorite"
      class="app-modal-backdrop gear-backdrop"
      :class="{ 'is-closing': closingGear }"
      @click.self="closeGear"
    >
      <section class="app-modal gear-modal" role="dialog" aria-modal="true" aria-labelledby="gear-modal-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Character Gear</p>
            <h2 id="gear-modal-title">{{ activeGearFavorite.profile.name }}</h2>
          </div>
          <button class="btn-icon-outline" type="button" aria-label="Close gear" @click="closeGear">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <section class="armory-frame">
          <div class="armory-character">
            <img :src="armoryImage" alt="" aria-hidden="true">
          </div>

          <div class="armory-column armory-left">
            <a
              v-for="slot in leftGearSlots"
              :key="slot"
              class="armory-item"
              :class="{ 'is-empty': !gearItemBySlot(activeGearFavorite.profile, slot) }"
              :href="wowheadItemUrl(gearItemBySlot(activeGearFavorite.profile, slot))"
              :data-wowhead="wowheadItemData(gearItemBySlot(activeGearFavorite.profile, slot))"
              target="_blank"
              rel="noreferrer"
              :title="gearItemName(activeGearFavorite.profile, slot)"
            >
              <img v-if="gearItemBySlot(activeGearFavorite.profile, slot)" :src="itemIconUrl(gearItemBySlot(activeGearFavorite.profile, slot))" :alt="gearItemName(activeGearFavorite.profile, slot)" loading="eager">
              <span>
                <strong>{{ gearItemName(activeGearFavorite.profile, slot) }}</strong>
                <small>{{ gearItemLevel(activeGearFavorite.profile, slot) }}</small>
              </span>
            </a>
          </div>

          <div class="armory-column armory-right">
            <a
              v-for="slot in rightGearSlots"
              :key="slot"
              class="armory-item is-right"
              :class="{ 'is-empty': !gearItemBySlot(activeGearFavorite.profile, slot) }"
              :href="wowheadItemUrl(gearItemBySlot(activeGearFavorite.profile, slot))"
              :data-wowhead="wowheadItemData(gearItemBySlot(activeGearFavorite.profile, slot))"
              target="_blank"
              rel="noreferrer"
              :title="gearItemName(activeGearFavorite.profile, slot)"
            >
              <span>
                <strong>{{ gearItemName(activeGearFavorite.profile, slot) }}</strong>
                <small>{{ gearItemLevel(activeGearFavorite.profile, slot) }}</small>
              </span>
              <img v-if="gearItemBySlot(activeGearFavorite.profile, slot)" :src="itemIconUrl(gearItemBySlot(activeGearFavorite.profile, slot))" :alt="gearItemName(activeGearFavorite.profile, slot)" loading="eager">
            </a>
          </div>

          <div class="armory-weapons">
            <a
              v-for="slot in weaponGearSlots"
              :key="slot"
              class="armory-item armory-weapon"
              :class="{ 'is-empty': !gearItemBySlot(activeGearFavorite.profile, slot) }"
              :href="wowheadItemUrl(gearItemBySlot(activeGearFavorite.profile, slot))"
              :data-wowhead="wowheadItemData(gearItemBySlot(activeGearFavorite.profile, slot))"
              target="_blank"
              rel="noreferrer"
              :title="gearItemName(activeGearFavorite.profile, slot)"
            >
              <img v-if="gearItemBySlot(activeGearFavorite.profile, slot)" :src="itemIconUrl(gearItemBySlot(activeGearFavorite.profile, slot))" :alt="gearItemName(activeGearFavorite.profile, slot)" loading="eager">
              <span>
                <strong>{{ gearItemName(activeGearFavorite.profile, slot) }}</strong>
                <small>{{ gearItemLevel(activeGearFavorite.profile, slot) }}</small>
              </span>
            </a>
          </div>
        </section>
      </section>
    </div>

    <div
      v-if="activeTalentsFavorite"
      class="app-modal-backdrop talents-backdrop"
      :class="{ 'is-closing': closingTalents }"
      @click.self="closeTalents"
    >
      <section class="app-modal talents-modal" role="dialog" aria-modal="true" aria-labelledby="talents-modal-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Wowhead Talent Calculator</p>
            <h2 id="talents-modal-title">{{ activeTalentsFavorite.profile.name }}</h2>
          </div>
          <div class="modal-actions">
            <button class="btn-icon-outline" type="button" aria-label="Close talents" @click="closeTalents">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </header>
        <section v-if="talentLoading" class="talents-placeholder">
          <i class="fa-solid fa-spinner"></i>
          <span>Loading talents...</span>
        </section>

        <section v-else-if="talentEmbedUrl(activeTalentsFavorite.profile)" class="talents-frame">
          <iframe
            :src="talentEmbedUrl(activeTalentsFavorite.profile)"
            title="Wowhead talent calculator"
            loading="lazy"
            scrolling="no"
            referrerpolicy="no-referrer-when-downgrade"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          ></iframe>
        </section>

        <section v-else class="talents-placeholder">
          <i class="fa-solid fa-circle-info"></i>
          <span>Talent loadout is not available for this character yet.</span>
        </section>
        
        <a
            v-if="talentCalculatorUrl(activeTalentsFavorite.profile)"
            class="btn-sm-outline"
            :href="talentCalculatorUrl(activeTalentsFavorite.profile)"
            target="_blank"
            rel="noreferrer"
        >
            <i class="fa-solid fa-arrow-up-right-from-square"></i> Open Wowhead
        </a>
      </section>
    </div>
  </section>
</template>

<script>
import armoryImage from '../assets/images/armory.png'
import allianceBanner from '../assets/images/alliance-banner.svg'
import allianceHordeBanner from '../assets/images/alliance-horde-banner.svg'
import hordeBanner from '../assets/images/horde-banner.svg'
import rioServiceIcon from '../assets/images/services/rio-small-logo.svg'
import warcraftLogsServiceIcon from '../assets/images/services/warcraftlogs-small-logo.png'
import wowServiceIcon from '../assets/images/services/wow-small-logo.svg'
import deathKnightIcon from '../assets/images/classes/death knight.png'
import demonHunterIcon from '../assets/images/classes/demon hunter.png'
import druidIcon from '../assets/images/classes/druid.png'
import evokerIcon from '../assets/images/classes/evoker.png'
import hunterIcon from '../assets/images/classes/hunter.png'
import mageIcon from '../assets/images/classes/mage.png'
import monkIcon from '../assets/images/classes/monk.png'
import paladinIcon from '../assets/images/classes/paladin.png'
import priestIcon from '../assets/images/classes/priest.png'
import rogueIcon from '../assets/images/classes/rogue.png'
import shamanIcon from '../assets/images/classes/shaman.png'
import warlockIcon from '../assets/images/classes/warlock.png'
import warriorIcon from '../assets/images/classes/warrior.png'
import AppEmptyState from '../components/AppEmptyState.vue'
import { getCharacterProfile, getMythicPlusStaticData } from '../services/raiderApi'
import { getBestMythicScore, parseCharacterUrl, summarizeRaidProgress } from '../utils/formatters'
import { ensureWowheadTooltips, refreshWowheadTooltips } from '../utils/wowheadTooltips'

const ICON_BASE_URL = 'https://render.worldofwarcraft.com/us/icons/56'
const CDN_BASE_URL = 'https://cdn.raiderio.net'

const CLASS_ICONS = {
  'death knight': deathKnightIcon,
  deathknight: deathKnightIcon,
  'demon hunter': demonHunterIcon,
  demonhunter: demonHunterIcon,
  druid: druidIcon,
  evoker: evokerIcon,
  hunter: hunterIcon,
  mage: mageIcon,
  monk: monkIcon,
  paladin: paladinIcon,
  priest: priestIcon,
  rogue: rogueIcon,
  shaman: shamanIcon,
  warlock: warlockIcon,
  warrior: warriorIcon,
}

const CLASS_COLORS = {
  'death knight': '#c41e3a',
  deathknight: '#c41e3a',
  'demon hunter': '#a330c9',
  demonhunter: '#a330c9',
  druid: '#ff7c0a',
  evoker: '#33937f',
  hunter: '#aad372',
  mage: '#3fc7eb',
  monk: '#00ff98',
  paladin: '#f48cba',
  priest: '#ffffff',
  rogue: '#fff468',
  shaman: '#0070dd',
  warlock: '#8788ee',
  warrior: '#c69b6d',
}

const FACTION_ICONS = {
  alliance: allianceBanner,
  horde: hordeBanner,
  mixed: allianceHordeBanner,
}

const DEFAULT_FAVORITE_FILTERS = {
  search: '',
  sortBy: 'addedAt',
}
const FAVORITES_FILTERS_KEY = 'rio-dashboard-favorites-filters'
const FAVORITE_AUTO_REFRESH_AGE_MS = 24 * 60 * 60 * 1000

function loadFavoriteFilters() {
  try {
    const saved = JSON.parse(localStorage.getItem(FAVORITES_FILTERS_KEY) || '{}')
    return {
      search: typeof saved.search === 'string' ? saved.search : DEFAULT_FAVORITE_FILTERS.search,
      sortBy: ['addedAt', 'score', 'itemLevel', 'name'].includes(saved.sortBy) ? saved.sortBy : DEFAULT_FAVORITE_FILTERS.sortBy,
    }
  } catch (error) {
    return { ...DEFAULT_FAVORITE_FILTERS }
  }
}

export default {
  name: 'FavoritesView',
  components: {
    AppEmptyState,
  },
  data() {
    const savedFilters = loadFavoriteFilters()

    return {
      armoryImage,
      characterUrl: '',
      search: savedFilters.search,
      sortBy: savedFilters.sortBy,
      draftSearch: savedFilters.search,
      draftSortBy: savedFilters.sortBy,
      message: '',
      loading: false,
      refreshingAll: false,
      autoRefreshChecked: false,
      isFiltersOpen: false,
      closingFilters: false,
      activeRunsFavorite: null,
      runsMode: 'best',
      activeGearFavorite: null,
      activeTalentsFavorite: null,
      closingRuns: false,
      closingGear: false,
      closingTalents: false,
      talentLoading: false,
      activeMenuFavoriteId: null,
      dungeons: [],
      leftGearSlots: ['head', 'neck', 'shoulder', 'back', 'chest', 'shirt', 'tabard', 'wrist'],
      rightGearSlots: ['hands', 'waist', 'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2'],
      weaponGearSlots: ['mainhand', 'offhand'],
    }
  },
  computed: {
    favorites() {
      return this.$store.getters['getFavorites']
    },
    isDarkMode() {
      return this.$store.getters['getThemeMode'] === 'dark'
    },
    sortedFavorites() {
      const needle = this.search.toLowerCase()

      return [...this.favorites]
        .filter((item) => {
          if (!needle) {
            return true
          }
          const text = [item.profile.name, item.profile.class, item.profile.realm, item.profile.guild?.name]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return text.includes(needle)
        })
        .sort((left, right) => {
          if (this.sortBy === 'score') {
            return getBestMythicScore(right.profile) - getBestMythicScore(left.profile)
          }
          if (this.sortBy === 'itemLevel') {
            return (right.profile.gear?.item_level_equipped || 0) - (left.profile.gear?.item_level_equipped || 0)
          }
          if (this.sortBy === 'name') {
            return left.profile.name.localeCompare(right.profile.name, 'ru')
          }
          return new Date(right.addedAt).getTime() - new Date(left.addedAt).getTime()
        })
    },
  },
  mounted() {
    this.ensureWowheadTooltips()
    this.loadDungeonIcons()
    this.refreshStaleFavorites()
    document.addEventListener('click', this.closeFavoriteMenuOnOutsideClick)
    window.addEventListener('keydown', this.closeOpenDialogOnEscape)
  },
  watch: {
    search() {
      this.persistFavoriteFilters()
    },
    sortBy() {
      this.persistFavoriteFilters()
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeFavoriteMenuOnOutsideClick)
    window.removeEventListener('keydown', this.closeOpenDialogOnEscape)
    document.body.classList.remove('is-scroll-locked')
  },
  methods: {
    addFavorite(character, source = 'manual', silent = false) {
      this.$store.dispatch('addFavorite', { character, source, silent })
    },
    removeFavorite(id) {
      const favorite = this.favorites.find((item) => item.id === id)
      this.$store.dispatch('requestConfirmation', {
        title: 'Remove favorite?',
        message: `Remove ${favorite?.profile?.name || 'this character'} from favorites and compare?`,
        confirmLabel: 'Remove',
        action: 'removeFavorite',
        payload: id,
      })
    },
    addToCompare(favorite) {
      this.$store.dispatch('addToCompare', favorite)
    },
    toggleFavoriteMenu(id) {
      this.activeMenuFavoriteId = this.activeMenuFavoriteId === id ? null : id
    },
    closeFavoriteMenuOnOutsideClick() {
      this.activeMenuFavoriteId = null
    },
    addToCompareFromMenu(favorite) {
      this.addToCompare(favorite)
      this.activeMenuFavoriteId = null
    },
    removeFavoriteFromMenu(id) {
      this.removeFavorite(id)
      this.activeMenuFavoriteId = null
    },
    refreshFavoriteFromMenu(favorite) {
      this.activeMenuFavoriteId = null
      this.refreshFavorite(favorite)
    },
    favoriteUpdatedAt(favorite) {
      return favorite.refreshedAt || favorite.updatedAt || favorite.addedAt || favorite.profile?.last_crawled_at || ''
    },
    isFavoriteStale(favorite) {
      const updatedAt = this.favoriteUpdatedAt(favorite)
      const timestamp = updatedAt ? new Date(updatedAt).getTime() : 0
      if (!timestamp || Number.isNaN(timestamp)) {
        return true
      }
      return Date.now() - timestamp > FAVORITE_AUTO_REFRESH_AGE_MS
    },
    refreshStaleFavorites() {
      if (this.autoRefreshChecked || !this.favorites.length) return
      this.autoRefreshChecked = true

      const staleFavorites = this.favorites.filter((favorite) => this.isFavoriteStale(favorite))
      if (!staleFavorites.length) return

      this.refreshAllFavorites(true, staleFavorites)
    },
    openFilters() {
      this.draftSearch = this.search
      this.draftSortBy = this.sortBy
      this.closingFilters = false
      this.isFiltersOpen = true
      document.body.classList.add('is-scroll-locked')
    },
    closeFilters() {
      if (!this.isFiltersOpen || this.closingFilters) return
      this.closingFilters = true
      window.setTimeout(() => {
        this.isFiltersOpen = false
        this.closingFilters = false
        document.body.classList.remove('is-scroll-locked')
      }, 180)
    },
    persistFavoriteFilters() {
      localStorage.setItem(FAVORITES_FILTERS_KEY, JSON.stringify({
        search: this.search,
        sortBy: this.sortBy,
      }))
    },
    applyFilters() {
      this.search = this.draftSearch
      this.sortBy = this.draftSortBy
      this.closeFilters()
    },
    resetFilters() {
      this.search = DEFAULT_FAVORITE_FILTERS.search
      this.sortBy = DEFAULT_FAVORITE_FILTERS.sortBy
      this.draftSearch = DEFAULT_FAVORITE_FILTERS.search
      this.draftSortBy = DEFAULT_FAVORITE_FILTERS.sortBy
      this.closeFilters()
    },
    openRuns(favorite) {
      this.closingRuns = false
      this.runsMode = 'best'
      this.activeRunsFavorite = favorite
      this.lockPageScroll()
    },
    closeRuns() {
      if (!this.activeRunsFavorite || this.closingRuns) return
      this.closingRuns = true
      window.setTimeout(() => {
        this.activeRunsFavorite = null
        this.closingRuns = false
        this.releaseModalScroll()
      }, 190)
    },
    openGear(favorite) {
      this.closingGear = false
      this.activeGearFavorite = favorite
      this.lockPageScroll()
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
    closeGear() {
      if (!this.activeGearFavorite || this.closingGear) return
      this.closingGear = true
      window.setTimeout(() => {
        this.activeGearFavorite = null
        this.closingGear = false
        this.releaseModalScroll()
      }, 190)
    },
    async openTalents(favorite) {
      this.closingTalents = false
      this.activeTalentsFavorite = favorite
      this.lockPageScroll()

      if (this.talentLoadoutText(favorite.profile)) {
        return
      }

      this.talentLoading = true
      const loadingId = await this.$store.dispatch('startGlobalLoading', 'Loading talents...')
      try {
        const profile = await this.fetchProfile({
          region: favorite.profile.region,
          realm: favorite.profile.realm,
          name: favorite.profile.name,
        })
        this.activeTalentsFavorite = {
          ...favorite,
          profile,
        }
      } catch (error) {
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Talents unavailable',
          description: `Unable to load talents for ${favorite.profile.name}.`,
        })
      } finally {
        this.talentLoading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    closeTalents() {
      if (!this.activeTalentsFavorite || this.closingTalents) return
      this.closingTalents = true
      window.setTimeout(() => {
        this.activeTalentsFavorite = null
        this.closingTalents = false
        this.talentLoading = false
        this.releaseModalScroll()
      }, 190)
    },
    lockPageScroll() {
      document.body.classList.add('is-scroll-locked')
    },
    releaseModalScroll() {
      if (this.activeRunsFavorite || this.activeGearFavorite || this.activeTalentsFavorite) {
        return
      }
      document.body.classList.remove('is-scroll-locked')
    },
    closeOpenDialogOnEscape(event) {
      if (event.key !== 'Escape') return

      if (this.activeTalentsFavorite) {
        this.closeTalents()
        return
      }

      if (this.activeGearFavorite) {
        this.closeGear()
        return
      }

      if (this.activeRunsFavorite) {
        this.closeRuns()
        return
      }

      if (this.isFiltersOpen) {
        this.closeFilters()
        return
      }

      this.activeMenuFavoriteId = null
    },
    async loadDungeonIcons() {
      try {
        const results = await Promise.allSettled([11, 10, 9, 8].map((expansionId) => getMythicPlusStaticData(expansionId)))
        this.dungeons = results
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value.dungeons || [])
      } catch (error) {
        this.dungeons = []
      }
    },
    showComingSoon() {
      this.$store.dispatch('notify', {
        type: 'warning',
        title: 'Coming soon',
        description: 'Talents view is not available yet.',
      })
    },
    async addByUrl() {
      if (!this.characterUrl) {
        this.message = 'Paste a character URL.'
        this.$store.dispatch('notify', {
          type: 'warning',
          title: 'Character URL required',
          description: 'Paste a Raider.io character URL before adding a favorite.',
        })
        return
      }

      this.loading = true
      this.message = ''
      const loadingId = await this.$store.dispatch('startGlobalLoading', 'Loading character profile...')

      try {
        const parsed = parseCharacterUrl(this.characterUrl)
        const profile = await this.fetchProfile(parsed)
        this.addFavorite(profile, 'manual')
        this.characterUrl = ''
        this.message = `${profile.name} has been added to favorites.`
      } catch (error) {
        this.message = error.message || 'Unable to add character.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Unable to add character',
          description: this.message,
        })
      } finally {
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    async refreshFavorite(favorite) {
      this.loading = true
      this.message = ''
      const loadingId = await this.$store.dispatch('startGlobalLoading', 'Refreshing character profile...')

      try {
        const profile = await this.fetchProfile({
          region: favorite.profile.region,
          realm: favorite.profile.realm,
          name: favorite.profile.name,
        })
        this.addFavorite(profile, favorite.source, true)
        this.message = `${profile.name} profile has been refreshed.`
        this.$store.dispatch('notify', {
          type: 'success',
          title: 'Favorite refreshed',
          description: `${profile.name} profile has been refreshed.`,
        })
      } catch (error) {
        this.message = `Unable to refresh ${favorite.profile.name}.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Refresh failed',
          description: this.message,
        })
      } finally {
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    async refreshAllFavorites(isAutomatic = false, favorites = this.favorites) {
      const targets = favorites.filter(Boolean)
      if (!targets.length || this.refreshingAll) return

      this.refreshingAll = true
      this.loading = true
      this.message = ''
      const loadingId = await this.$store.dispatch(
        'startGlobalLoading',
        isAutomatic ? 'Refreshing stale character profiles...' : 'Refreshing all character profiles...'
      )

      const failedNames = []
      let refreshedCount = 0

      try {
        for (const favorite of targets) {
          try {
            const profile = await this.fetchProfile({
              region: favorite.profile.region,
              realm: favorite.profile.realm,
              name: favorite.profile.name,
            })
            this.addFavorite(profile, favorite.source, true)
            refreshedCount += 1
          } catch (error) {
            failedNames.push(favorite.profile?.name || 'Unknown')
          }
        }

        if (refreshedCount || failedNames.length) {
          this.$store.dispatch('notify', {
            type: failedNames.length ? 'warning' : 'success',
            title: isAutomatic ? 'Favorites auto-refreshed' : 'Favorites refreshed',
            description: failedNames.length
              ? `${refreshedCount}/${targets.length} refreshed. Failed: ${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? '...' : ''}.`
              : `${refreshedCount} character profile${refreshedCount === 1 ? '' : 's'} refreshed.`,
          })
        }
      } finally {
        this.refreshingAll = false
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    fetchProfile(parsed) {
      return getCharacterProfile({
        region: parsed.region,
        realm: parsed.realm,
        name: parsed.name,
        fields: [
          'gear',
          'talents',
          'guild',
          'raid_progression:current-expansion',
          'mythic_plus_scores_by_season:current',
          'mythic_plus_best_runs:all',
          'mythic_plus_recent_runs',
          'mythic_plus_ranks',
          'realm',
        ].join(','),
      })
    },
    ensureWowheadTooltips() {
      ensureWowheadTooltips()
    },
    refreshWowheadTooltips() {
      this.$nextTick(() => {
        refreshWowheadTooltips()
      })
    },
    bestScore(profile) {
      const score = getBestMythicScore(profile)
      return score ? Math.round(score).toString() : '-'
    },
    itemLevel(profile) {
      const level = profile.gear?.item_level_equipped
      return level ? Math.round(Number(level)).toString() : '-'
    },
    mythicPlusSpecRank(profile, scope) {
      const ranks = profile.mythic_plus_ranks || {}
      const role = String(profile.active_spec_role || '').toLowerCase()
      const spec = String(profile.active_spec_name || '').toLowerCase().replace(/[\s-]+/g, '_')
      const characterClass = String(profile.class || '').toLowerCase().replace(/[\s-]+/g, '_')
      const candidates = [
        spec && `spec_${spec}`,
        spec,
        characterClass && spec && `${characterClass}_${spec}`,
        role && `class_${role}`,
        role,
        'class',
        'overall',
      ].filter(Boolean)

      const rankGroup = candidates.map((key) => ranks[key]).find((item) => item?.[scope])
      return rankGroup ? String(rankGroup[scope]) : '-'
    },
    raidProgressLabel(profile) {
      return summarizeRaidProgress(profile)[0]?.summary || '-'
    },
    talentLoadoutText(profile) {
      return profile.talentLoadout?.loadout_text || profile.talent_loadout?.loadout_text || profile.talents?.loadout_text || ''
    },
    talentCalculatorUrl(profile) {
      const loadoutText = this.talentLoadoutText(profile)
      return loadoutText ? `https://www.wowhead.com/talent-calc/blizzard/${encodeURIComponent(loadoutText)}` : ''
    },
    talentEmbedUrl(profile) {
      const loadoutText = this.talentLoadoutText(profile)
      return loadoutText ? `https://www.wowhead.com/talent-calc/embed/blizzard/${encodeURIComponent(loadoutText)}` : ''
    },
    factionKey(faction) {
      const key = String(faction || '').toLowerCase()
      return key === 'alliance' || key === 'horde' ? key : 'mixed'
    },
    factionIcon(faction) {
      return FACTION_ICONS[this.factionKey(faction)]
    },
    serviceLinks(profile) {
      return [
        {
          key: 'warcraftlogs',
          label: 'Warcraft Logs',
          icon: warcraftLogsServiceIcon,
          href: this.warcraftLogsUrl(profile),
        },
        {
          key: 'raiderio',
          label: 'Raider.io',
          icon: rioServiceIcon,
          href: profile.profile_url || this.raiderIoUrl(profile),
        },
        {
          key: 'worldofwarcraft',
          label: 'World of Warcraft',
          icon: wowServiceIcon,
          href: this.worldOfWarcraftUrl(profile),
        },
      ]
    },
    externalUrl(profile, keys) {
      const directUrl = keys.map((key) => profile[key]).find(Boolean)
      if (directUrl) return directUrl

      const linkSources = [profile.links, profile.external_links, profile.externalLinks].filter(Boolean)
      for (const source of linkSources) {
        if (Array.isArray(source)) {
          const match = source.find((item) => {
            const haystack = [item.key, item.type, item.name, item.label, item.slug].filter(Boolean).join(' ').toLowerCase()
            return keys.some((key) => haystack.includes(key.replace(/_/g, '')))
          })
          if (match?.url || match?.href) return match.url || match.href
          continue
        }

        const nestedUrl = keys.map((key) => source[key]?.url || source[key]?.href || source[key]).find(Boolean)
        if (nestedUrl) return nestedUrl
      }

      return ''
    },
    encodedCharacterPath(profile) {
      return {
        region: encodeURIComponent(String(profile.region || '').toLowerCase()),
        realm: encodeURIComponent(String(profile.realm || '').toLowerCase().replace(/\s+/g, '-')),
        name: encodeURIComponent(String(profile.name || '').toLowerCase()),
      }
    },
    raiderIoUrl(profile) {
      const path = this.encodedCharacterPath(profile)
      return `https://raider.io/characters/${path.region}/${path.realm}/${path.name}`
    },
    warcraftLogsUrl(profile) {
      return this.externalUrl(profile, [
        'warcraftlogs_url',
        'warcraft_logs_url',
        'warcraftLogsUrl',
        'wcl_url',
      ]) || (() => {
        const path = this.encodedCharacterPath(profile)
        return `https://www.warcraftlogs.com/character/${path.region}/${path.realm}/${path.name}`
      })()
    },
    worldOfWarcraftUrl(profile) {
      return this.externalUrl(profile, [
        'worldofwarcraft_url',
        'world_of_warcraft_url',
        'wow_profile_url',
        'armory_url',
        'blizzard_url',
      ]) || (() => {
        const path = this.encodedCharacterPath(profile)
        const locale = path.region === 'eu' ? 'en-gb' : 'en-us'
        return `https://worldofwarcraft.blizzard.com/${locale}/character/${path.region}/${path.realm}/${path.name}`
      })()
    },
    classIcon(characterClass) {
      const key = String(characterClass || '').toLowerCase()
      return CLASS_ICONS[key.replace(/-/g, ' ')] || CLASS_ICONS[key.replace(/\s/g, '')] || ''
    },
    favoriteNameStyle(characterClass) {
      if (!this.isDarkMode) {
        return null
      }

      const key = String(characterClass || '').toLowerCase()
      const color = CLASS_COLORS[key.replace(/-/g, ' ')] || CLASS_COLORS[key.replace(/\s/g, '')]
      return color ? { color } : null
    },
    bestRuns(profile) {
      return profile.mythic_plus_best_runs || []
    },
    recentRuns(profile) {
      return (profile.mythic_plus_recent_runs || []).slice(0, 10)
    },
    activeRuns(profile) {
      return this.runsMode === 'recent' ? this.recentRuns(profile) : this.bestRuns(profile)
    },
    formatScore(score) {
      return Number(score || 0).toFixed(1)
    },
    runTime(run) {
      const value = run.clear_time_ms || run.time_ms
      if (!value) return '-'
      const totalSeconds = Math.floor(value / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes}:${String(seconds).padStart(2, '0')}`
    },
    upgradeStars(run) {
      const upgrades = run.num_keystone_upgrades || 0
      return upgrades ? '+'.repeat(upgrades) : '-'
    },
    wowheadAffixUrl(affix) {
      return affix?.id ? `https://www.wowhead.com/affix=${affix.id}` : 'https://www.wowhead.com/'
    },
    normalizeAssetUrl(url) {
      if (!url) return ''
      return url.startsWith('http') ? url : `${CDN_BASE_URL}${url}`
    },
    dungeonRunName(run) {
      return typeof run.dungeon === 'string' ? run.dungeon : run.dungeon?.name || '-'
    },
    dungeonRunIcon(run) {
      const directIcon = run.dungeon_icon_url || run.dungeon?.icon_url || run.icon_url
      if (directIcon) return this.normalizeAssetUrl(directIcon)

      const dungeonName = this.dungeonRunName(run).toLowerCase()
      const dungeonSlug = String(run.dungeon_slug || run.dungeon?.slug || run.short_name || '')
        .toLowerCase()
        .replace(/\s/g, '-')
      const dungeon = this.dungeons.find((item) => {
        return item.slug === dungeonSlug || String(item.name || '').toLowerCase() === dungeonName
      })
      return this.normalizeAssetUrl(dungeon?.icon_url || dungeon?.background_image_url)
    },
    affixIcon(affix) {
      if (!affix?.icon) return ''
      const icon = String(affix.icon)
      return icon.startsWith('http') ? icon : `${CDN_BASE_URL}/images/wow/icons/large/${icon}.jpg`
    },
    normalizedGearItems(profile) {
      const items = profile.gear?.items
      if (!items) return []

      if (Array.isArray(items)) {
        return items.map((item, index) => this.normalizeGearItem(item, item.slot || item.slot_key || `${index}`))
      }

      return Object.entries(items).map(([slotKey, item]) => this.normalizeGearItem(item, slotKey))
    },
    normalizeGearItem(item, slotKey) {
      return {
        ...(item || {}),
        slotKey: this.normalizeSlotKey(slotKey || item?.slot || item?.inventory_type?.type),
      }
    },
    normalizeSlotKey(slot) {
      return String(slot || '')
        .toLowerCase()
        .replace(/_/g, '')
        .replace(/-/g, '')
        .replace(/\s/g, '')
    },
    gearItemBySlot(profile, slot) {
      const normalizedSlot = this.normalizeSlotKey(slot)
      return this.normalizedGearItems(profile).find((item) => item.slotKey === normalizedSlot)
    },
    weaponItem(profile) {
      return this.weaponItems(profile)[0]
    },
    weaponItems(profile) {
      return ['mainhand', 'offhand']
        .map((slot) => this.gearItemBySlot(profile, slot))
        .filter(Boolean)
    },
    trinketItems(profile) {
      return ['trinket1', 'trinket2']
        .map((slot) => this.gearItemBySlot(profile, slot))
        .filter(Boolean)
    },
    trinketSlots(profile) {
      return ['trinket1', 'trinket2'].map((slot) => {
        return this.gearItemBySlot(profile, slot) || {
          slotKey: slot,
          name: this.slotLabel(slot),
        }
      })
    },
    gearItemName(profile, slot) {
      return this.gearItemBySlot(profile, slot)?.name || this.slotLabel(slot)
    },
    gearItemLevel(profile, slot) {
      const item = this.gearItemBySlot(profile, slot)
      return item?.item_level || item?.itemLevel || '-'
    },
    slotLabel(slot) {
      const labels = {
        head: 'Head',
        neck: 'Neck',
        shoulder: 'Shoulder',
        back: 'Back',
        chest: 'Chest',
        shirt: 'Shirt',
        tabard: 'Tabard',
        wrist: 'Wrist',
        hands: 'Hands',
        waist: 'Waist',
        legs: 'Legs',
        feet: 'Feet',
        finger1: 'Finger',
        finger2: 'Finger',
        trinket1: 'Trinket',
        trinket2: 'Trinket',
        mainhand: 'Main hand',
        offhand: 'Off hand',
      }
      return labels[slot] || 'Empty slot'
    },
    itemIconUrl(item) {
      if (!item?.icon) return ''
      const icon = String(item.icon)
      if (icon.startsWith('http')) return icon
      return `${ICON_BASE_URL}/${icon}.jpg`
    },
    wowheadItemUrl(item) {
      const itemId = item?.item_id || item?.id
      return itemId ? `https://www.wowhead.com/item=${itemId}` : 'https://www.wowhead.com/'
    },
    wowheadItemData(item) {
      const itemId = item?.item_id || item?.id
      if (!itemId) return null

      const params = [`item=${itemId}`]
      const itemLevel = item.item_level || item.itemLevel
      if (itemLevel) params.push(`ilvl=${itemLevel}`)
      if (Array.isArray(item.bonuses) && item.bonuses.length) params.push(`bonus=${item.bonuses.join(':')}`)
      if (Array.isArray(item.gems) && item.gems.length) params.push(`gems=${item.gems.map((gem) => gem.item_id || gem.id || gem).join(':')}`)
      if (item.enchant?.id || item.enchant_id) params.push(`ench=${item.enchant?.id || item.enchant_id}`)
      return params.join('&')
    },
  },
}
</script>
